import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PAGE_SIZE_OPTIONS } from '@/constants/pagination';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Close, Search, Warning as WarningIcon, West as WestIcon } from '@mui/icons-material';
import { Alert } from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { ASSET_STATUS_KEYS } from '@equip/utils';
import { WorkflowModalLayout } from '@/components/shared/workflows/WorkflowModalLayout';
import { WorkflowCartRow } from '@/components/shared/workflows/WorkflowCartRow';
import { useAssetInventory, type AssetInventoryGroup } from '@/hooks/useAssetInventory';
import { useCatalogMaintenanceEligible } from '@/hooks/queries/catalog/useCatalogMaintenanceEligible';
import { useAuth } from '@/hooks/useAuth';
import { ViewerGuardedButton } from '@/components/shared/ViewerGuardedButton';
import { groupMaintenanceCartItems } from '@/components/maintenance/groupMaintenanceCartItems';
import {
  useSendForMaintenance,
  useMaintenanceCart,
  useAddToMaintenanceCart,
  useRemoveFromMaintenanceCart,
  useClearMaintenanceCart,
  maintenanceCartQueryKeys,
} from '@/hooks/mutations/maintenance';
import { openDocumentWithAuth, preOpenWindow } from '@/utils/pdfHelpers';
import { useQueryClient } from '@tanstack/react-query';
import { cartQueryKeys } from '@/hooks/queries/cart/useCartQueries';
import { useNotifications } from '@/stores/useNotificationStore';
import { cartService } from '@/services/cart';
import { httpClient } from '@/utils/httpClient';
import { API_CONFIG } from '@/config/api';
import { multiWordMatch } from '@/utils/multiWordMatch';

export interface SendForMaintenanceModalProps {
  open: boolean;
  onClose: () => void;
}

const clampQuantity = (value: number, max: number): number => {
  if (!Number.isFinite(value)) {
    return 1;
  }
  return Math.min(Math.max(Math.floor(value), 1), Math.max(max, 1));
};

export const SendForMaintenanceModal: React.FC<SendForMaintenanceModalProps> = ({
  open,
  onClose,
}) => {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const workflowType = 'maintenance-send' as const;

  // Server-side cart state
  const { data: cartData } = useMaintenanceCart(userId, workflowType, {
    enabled: open && !!userId,
  });
  const { mutateAsync: addToCart, isPending: isAdding } = useAddToMaintenanceCart(workflowType);
  const { mutateAsync: removeFromCart, isPending: isRemoving } =
    useRemoveFromMaintenanceCart(workflowType);
  const { mutateAsync: clearCart } = useClearMaintenanceCart(workflowType);

  // Inventory and send mutation
  const {
    data = [],
    isLoading,
    isError,
    refetch,
  } = useAssetInventory({
    status_key: `eq.${ASSET_STATUS_KEYS.AVAILABLE}`,
  });
  const {
    data: maintenanceEligibleCatalogIds = [],
    isLoading: isLoadingCatalogFilter,
    isError: isCatalogFilterError,
    refetch: refetchCatalogFilter,
  } = useCatalogMaintenanceEligible({
    enabled: open,
  });
  const { mutateAsync: sendForMaintenance, isPending: isSending } = useSendForMaintenance();
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  const [searchText, setSearchText] = useState('');
  const [qtyDrafts, setQtyDrafts] = useState<Record<string, number>>({});
  const [documentNumber, setDocumentNumber] = useState('');
  const [showExitPrompt, setShowExitPrompt] = useState(false);
  const [isForceClosing, setIsForceClosing] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const [isCleaningStaleCart, setIsCleaningStaleCart] = useState(false);

  // Restore document number from a saved cart when the modal opens
  useEffect(() => {
    if (cartData?.cart?.document_number) {
      setDocumentNumber((prev) => prev || cartData.cart!.document_number!);
    }
  }, [cartData?.cart?.document_number]);

  const isPending = isAdding || isRemoving || isSending;

  // Get reserved asset IDs from server-side cart
  const reservedAssetIds = useMemo(() => {
    if (!cartData?.assets) return new Set<string>();
    return new Set(cartData.assets.map((a) => a.entity.id));
  }, [cartData?.assets]);

  // Detect stale cart: all reserved assets have been moved out of 'available' status
  const isStaleCart = useMemo(() => {
    if (!cartData?.assets || cartData.assets.length === 0) return false;
    return cartData.assets.every((a) => {
      const statusKey = a.entity.entity_data?.status_key;
      return typeof statusKey === 'string' && statusKey !== ASSET_STATUS_KEYS.AVAILABLE;
    });
  }, [cartData?.assets]);

  // Build cart items from server-side cart data for display
  const cartItems = useMemo(() => {
    if (!cartData?.assets) return [];
    return groupMaintenanceCartItems(cartData.assets);
  }, [cartData?.assets]);

  const cartCount = useMemo(
    () => cartItems.reduce((sum, item) => sum + item.quantity, 0),
    [cartItems],
  );

  const maintenanceEligibleCatalogSet = useMemo(
    () => new Set(maintenanceEligibleCatalogIds),
    [maintenanceEligibleCatalogIds],
  );

  const maintenanceEligibleRows = useMemo(() => {
    if (isLoadingCatalogFilter) {
      return [];
    }

    return data.filter((row) => {
      if (!row.catalog_id) {
        return true;
      }
      return maintenanceEligibleCatalogSet.has(row.catalog_id);
    });
  }, [data, isLoadingCatalogFilter, maintenanceEligibleCatalogSet]);

  // Filter inventory rows by search text
  const filteredRows = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query) {
      return maintenanceEligibleRows;
    }

    return maintenanceEligibleRows.filter((row) => {
      const parts = [
        row.asset_name,
        row.lin ?? '',
        row.nsn ?? '',
        row.location_name ?? '',
        row.location_path ?? '',
      ];
      return multiWordMatch(parts.join(' '), query);
    });
  }, [maintenanceEligibleRows, searchText]);

  const getAddableQty = useCallback(
    (row: AssetInventoryGroup) => {
      return row.asset_ids.reduce((count, id) => count + (reservedAssetIds.has(id) ? 0 : 1), 0);
    },
    [reservedAssetIds],
  );

  const resetFormState = useCallback(() => {
    setSearchText('');
    setQtyDrafts({});
    setDocumentNumber('');
  }, []);

  const handleCleanupStaleCart = useCallback(async () => {
    if (!userId || !cartData?.cart || isCleaningStaleCart) return;
    setIsCleaningStaleCart(true);
    try {
      await clearCart({ userId, cartId: cartData.cart.id, allowActiveScan: true });
      void queryClient.invalidateQueries({ queryKey: cartQueryKeys.activeAll() });
      notifications.success('Cart cleaned up', 'Stale maintenance cart has been removed.');
      resetFormState();
      onClose();
    } catch {
      notifications.error('Cleanup failed', 'Could not remove stale cart. Please try again.');
    } finally {
      setIsCleaningStaleCart(false);
    }
  }, [
    userId,
    cartData?.cart,
    isCleaningStaleCart,
    clearCart,
    queryClient,
    notifications,
    resetFormState,
    onClose,
  ]);

  const forceClose = useCallback(() => {
    setShowExitPrompt(false);
    setIsForceClosing(true);
  }, []);

  const handleClose = useCallback(() => {
    if (cartCount > 0) {
      setShowExitPrompt(true);
    } else {
      resetFormState();
      onClose();
    }
  }, [cartCount, resetFormState, onClose]);

  const handleSave = useCallback(async () => {
    // Persist document number to the cart so it survives across sessions
    if (cartData?.cart?.id && documentNumber.trim()) {
      try {
        await httpClient.patch(
          `${API_CONFIG.BASE_URL}/api/rest/data/v1/workflow_carts?id=eq.${cartData.cart.id}`,
          { document_number: documentNumber.trim() },
        );
      } catch {
        // Best-effort; cart items are already saved server-side
      }
    }
    void queryClient.invalidateQueries({ queryKey: cartQueryKeys.activeAll() });
    void queryClient.invalidateQueries({ queryKey: maintenanceCartQueryKeys.all });
    resetFormState();
    forceClose();
  }, [cartData?.cart?.id, documentNumber, queryClient, resetFormState, forceClose]);

  const handleCancelMaintenance = useCallback(async () => {
    if (isCanceling) return;
    setIsCanceling(true);
    try {
      if (userId && cartData?.cart) {
        await clearCart({ userId, cartId: cartData.cart.id });
        void queryClient.invalidateQueries({ queryKey: cartQueryKeys.activeAll() });
      }
      resetFormState();
      forceClose();
    } catch {
      // Best-effort cleanup
    } finally {
      setIsCanceling(false);
    }
  }, [isCanceling, userId, cartData?.cart, clearCart, queryClient, resetFormState, forceClose]);

  const handleQtyDraftChange = useCallback((rowId: string, rawValue: string, max: number) => {
    const parsed = Number.parseInt(rawValue, 10);
    setQtyDrafts((prev) => ({
      ...prev,
      [rowId]: clampQuantity(Number.isFinite(parsed) ? parsed : 1, max),
    }));
  }, []);

  const handleAddToCart = useCallback(
    async (row: AssetInventoryGroup) => {
      if (!userId) return;

      // Get asset IDs that aren't already in cart
      const availableAssetIds = row.asset_ids.filter((id) => !reservedAssetIds.has(id));
      const draft = qtyDrafts[row.id] ?? Math.max(availableAssetIds.length, 1);
      const quantity = clampQuantity(draft, availableAssetIds.length);
      const assetIdsToAdd = availableAssetIds.slice(0, quantity);

      if (assetIdsToAdd.length === 0) return;

      await addToCart({ userId, assetIds: assetIdsToAdd });
    },
    [userId, qtyDrafts, reservedAssetIds, addToCart],
  );

  const handleRemoveCartItem = useCallback(
    async (assetIds: string[]) => {
      if (!userId) return;
      await removeFromCart({ userId, assetIds });
    },
    [userId, removeFromCart],
  );

  const handleComplete = useCallback(async () => {
    const trimmedDocumentNumber = documentNumber.trim();
    if (!trimmedDocumentNumber || cartCount === 0) {
      return;
    }

    // Persist document number to the cart before attempting submission so it
    // survives if the send fails and the user resumes later (#29554).
    if (cartData?.cart?.id) {
      try {
        await cartService.updateDocumentNumber(cartData.cart.id, trimmedDocumentNumber);
      } catch {
        // Best-effort; proceed with send regardless
      }
    }

    const form2407Window = preOpenWindow();

    // Get all asset IDs from the cart
    const assetIds = cartItems.flatMap((item) => item.assetIds);
    try {
      const result = await sendForMaintenance({
        documentNumber: trimmedDocumentNumber,
        assetIds,
      });

      if (result.documentDownloadUrl) {
        await openDocumentWithAuth(result.documentDownloadUrl, form2407Window);
      } else if (form2407Window && !form2407Window.closed) {
        form2407Window.close();
      }

      // Open additional tabs for extra documents (multi-LIN/NSN maintenance sends)
      if (result.documentLinks && result.documentLinks.length > 1) {
        for (let i = 1; i < result.documentLinks.length; i++) {
          const link = result.documentLinks[i];
          if (link?.documentDownloadUrl) {
            const extraWindow = preOpenWindow();
            await openDocumentWithAuth(link.documentDownloadUrl, extraWindow);
          }
        }
      }

      // Remove only the successfully submitted assets from the cart, preserving
      // any remaining items for future submission (#29554).
      const submittedAssetIds = result.assetIds ?? assetIds;
      if (userId && cartData?.cart) {
        try {
          await cartService.removeReservationsByAssetIds(userId, workflowType, submittedAssetIds);
        } catch {
          // Fallback: try full cart clear if selective removal fails
          try {
            await clearCart({ userId, cartId: cartData.cart.id, allowActiveScan: true });
          } catch {
            notifications.warning(
              'Cart cleanup incomplete',
              'Maintenance send succeeded, but the cart could not be updated. It may appear in your saved carts.',
            );
          }
        }

        // If all items were submitted, delete the cart entity entirely
        if (submittedAssetIds.length >= assetIds.length) {
          try {
            await cartService.deleteCart(cartData.cart.id);
          } catch {
            // Best-effort cart entity cleanup
          }
        }
      }
      void queryClient.invalidateQueries({ queryKey: cartQueryKeys.activeAll() });
      void queryClient.invalidateQueries({ queryKey: maintenanceCartQueryKeys.all });
      resetFormState();
      onClose();
    } catch {
      if (form2407Window && !form2407Window.closed) {
        form2407Window.close();
      }
      // Error notification handled by mutation's onError
    }
  }, [
    cartItems,
    cartCount,
    documentNumber,
    sendForMaintenance,
    userId,
    cartData?.cart,
    workflowType,
    clearCart,
    notifications,
    queryClient,
    resetFormState,
    onClose,
  ]);

  const plannerToolbar = (
    <TextField
      value={searchText}
      onChange={(event) => setSearchText(event.target.value)}
      placeholder="Search assets, LIN, NSN, location..."
      size="small"
      fullWidth
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Search fontSize="small" />
          </InputAdornment>
        ),
      }}
    />
  );

  const columns: GridColDef<AssetInventoryGroup>[] = [
    { field: 'asset_name', headerName: 'Asset Name', flex: 1, minWidth: 220 },
    { field: 'lin', headerName: 'LIN', width: 120 },
    { field: 'nsn', headerName: 'NSN', width: 150 },
    {
      field: 'location_name',
      headerName: 'Location',
      flex: 1.4,
      minWidth: 220,
      renderCell: (params) => (
        <Stack justifyContent="center" sx={{ height: '100%' }}>
          <Typography variant="body2" sx={{ lineHeight: 1.2 }}>
            {params.value}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ lineHeight: 1.2, fontSize: '0.7rem' }}
          >
            {params.row.location_path}
          </Typography>
        </Stack>
      ),
    },
    {
      field: 'stock_quantity',
      headerName: 'Available Qty',
      width: 130,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
    },
    {
      field: 'qty',
      headerName: 'Qty',
      width: 110,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const row = params.row;
        const remainingQty = getAddableQty(row);
        const draft = qtyDrafts[row.id];
        const value =
          remainingQty <= 0 ? 0 : draft != null ? clampQuantity(draft, remainingQty) : remainingQty;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <TextField
              size="small"
              type="number"
              value={value}
              disabled={remainingQty <= 0}
              slotProps={{ htmlInput: { min: 1, max: remainingQty } }}
              onChange={(event) => handleQtyDraftChange(row.id, event.target.value, remainingQty)}
              sx={{ width: 84 }}
            />
          </Box>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 130,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const row = params.row;
        const remainingQty = getAddableQty(row);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<Add fontSize="small" />}
              disabled={remainingQty <= 0 || isPending}
              onClick={() => void handleAddToCart(row)}
            >
              Add
            </Button>
          </Box>
        );
      },
    },
  ];

  const plannerContent =
    isError || isCatalogFilterError ? (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Failed to load available inventory.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={() => {
                void refetch();
                void refetchCatalogFilter();
              }}
            >
              Retry
            </Button>
          </Box>
        </Stack>
      </Paper>
    ) : (
      <Box sx={{ height: '100%', width: '100%' }}>
        <DataGrid
          rows={filteredRows}
          columns={columns}
          loading={isLoading || isLoadingCatalogFilter}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          disableRowSelectionOnClick
          rowHeight={60}
        />
      </Box>
    );

  const sidebarContent = (
    <Stack spacing={3}>
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          Document Number
        </Typography>
        <TextField
          value={documentNumber}
          onChange={(event) => setDocumentNumber(event.target.value)}
          placeholder="Enter document number"
          size="small"
          fullWidth
          required
        />
      </Box>

      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          Cart
        </Typography>
        <Paper variant="outlined" sx={{ p: 2, maxHeight: '40vh', overflowY: 'auto' }}>
          <Stack spacing={1.5}>
            {cartItems.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: 'center', py: 2 }}
              >
                No items added yet
              </Typography>
            ) : (
              cartItems.map((item) => (
                <WorkflowCartRow
                  key={item.key}
                  title={item.name}
                  subtitle={[
                    item.sizeLabel ? `Size ${item.sizeLabel}` : null,
                    item.lin !== '—' ? `LIN ${item.lin}` : null,
                    item.nsn !== '—' ? `NSN ${item.nsn}` : null,
                    `Qty ${item.quantity}`,
                  ]
                    .filter(Boolean)
                    .join(' • ')}
                  actions={
                    <IconButton
                      size="small"
                      color="primary"
                      aria-label={`Remove ${item.name} from cart`}
                      onClick={() => void handleRemoveCartItem(item.assetIds)}
                      disabled={isPending}
                    >
                      <Close fontSize="inherit" />
                    </IconButton>
                  }
                />
              ))
            )}
          </Stack>
        </Paper>
      </Box>

      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Total Items
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {cartCount} item{cartCount === 1 ? '' : 's'} selected
        </Typography>
      </Box>
    </Stack>
  );

  const footer = (
    <>
      <Button variant="outlined" onClick={handleClose} disabled={isPending}>
        Cancel
      </Button>
      <ViewerGuardedButton
        variant="contained"
        onClick={() => void handleComplete()}
        disabled={isPending || cartCount === 0 || documentNumber.trim().length === 0}
      >
        {isSending ? 'Sending…' : 'Complete'}
      </ViewerGuardedButton>
    </>
  );

  const closeButton = (
    <Button
      variant="outlined"
      startIcon={<WestIcon />}
      onClick={handleClose}
      sx={{ whiteSpace: 'nowrap' }}
    >
      Return to Maintenance
    </Button>
  );

  return (
    <>
      <WorkflowModalLayout
        open={open}
        onClose={handleClose}
        title="Send for Maintenance"
        plannerTitle="In-Stock Inventory"
        plannerToolbar={plannerToolbar}
        plannerContent={
          isStaleCart ? (
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Alert
                severity="warning"
                icon={<WarningIcon />}
                action={
                  <Button
                    color="warning"
                    size="small"
                    onClick={() => void handleCleanupStaleCart()}
                    disabled={isCleaningStaleCart}
                  >
                    {isCleaningStaleCart ? 'Cleaning up…' : 'Remove Stale Cart'}
                  </Button>
                }
              >
                This saved cart contains items that have already been processed (sent to
                maintenance). It can no longer be used. Click &ldquo;Remove Stale Cart&rdquo; to
                clean it up.
              </Alert>
            </Paper>
          ) : (
            plannerContent
          )
        }
        sidebarTitle="Send Summary"
        sidebarContent={sidebarContent}
        footer={footer}
        closeButton={closeButton}
      />

      <Dialog
        open={showExitPrompt}
        onClose={() => setShowExitPrompt(false)}
        TransitionProps={{
          onExited: () => {
            if (isForceClosing) {
              onClose();
              setIsForceClosing(false);
            }
          },
        }}
        aria-labelledby="exit-confirmation-title"
        aria-describedby="exit-confirmation-description"
      >
        <DialogTitle id="exit-confirmation-title">Items in Cart</DialogTitle>
        <DialogContent>
          <DialogContentText id="exit-confirmation-description">
            You have items in your maintenance send cart. You can save your progress for later or
            cancel to remove the reserved items.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => void handleCancelMaintenance()}
            color="error"
            disabled={isCanceling}
          >
            Cancel Maintenance
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setShowExitPrompt(false)} color="inherit" disabled={isCanceling}>
            Back to Cart
          </Button>
          <Button
            onClick={() => void handleSave()}
            variant="contained"
            color="primary"
            autoFocus
            disabled={isCanceling}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
